import { apiClient, ApiClient } from '@/shared/utils/api';
import { LoginRequest, LoginResponse, User } from '@/shared/types/api';
import { logger } from '@/utils/logger';
import { clearSkipFlag } from '@/config/roomConfig';
import { BACKEND_CONFIG } from '@/config/backendConfig';

class AuthService {
  private currentUser: User | null = null;

  constructor() {
    this.loadUser();
  }

  private loadUser(): void {
    // Try sessionStorage first (for staff users), then localStorage (for referral users)
    const userData = sessionStorage.getItem('user') || localStorage.getItem('user');
    if (userData) {
      try {
        this.currentUser = JSON.parse(userData);
      } catch (error) {
        logger.error('Failed to parse user data from storage', error);
        this.clearUser();
      }
    }
  }

  public reloadUser(): void {
    // Force reload user from localStorage (useful after login)
    this.currentUser = null;
    this.loadUser();
  }

  private saveUser(user: User): void {
    // For staff users, don't persist to localStorage - data comes from JWT only
    // Only persist for referral users who need offline access
    this.currentUser = user;
    if (user.user_type === 'referral' || user.user_type === 'referral_team') {
      localStorage.setItem('user', JSON.stringify(user));
    } else {
      // For staff users, save to sessionStorage for persistence during same session
      sessionStorage.setItem('user', JSON.stringify(user));
    }
  }

  private clearUser(): void {
    this.currentUser = null;
    localStorage.removeItem('user');
    sessionStorage.removeItem('user');
  }

  public async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const loginEndpoint = typeof BACKEND_CONFIG.endpoints.login === 'function'
        ? BACKEND_CONFIG.endpoints.login(BACKEND_CONFIG.isFastAPI)
        : BACKEND_CONFIG.endpoints.login;

      // Transform credentials for FastAPI if needed
      let payload: any = credentials;
      if (BACKEND_CONFIG.isFastAPI) {
        payload = {
          identifier: credentials.email, // FastAPI accepts email, employee_id, or phone
          password: credentials.password,
          tenant: BACKEND_CONFIG.getTenantValue()
        };
      }

      const response = await apiClient.post<LoginResponse>(loginEndpoint, payload);

      // FastAPI returns { access_token, refresh_token, staff, requires_password_change }
      // Django returns { access_token, refresh_token, user, must_change_password }
      const { access_token, refresh_token } = response.data;
      const user = (response.data as any).staff || (response.data as any).user;

      // Handle password change flag - FastAPI uses requires_password_change, Django uses must_change_password
      const requiresPasswordChange = (response.data as any).requires_password_change || (response.data as any).must_change_password || false;

      // Save tokens and user data
      apiClient.setTokens(access_token, refresh_token);
      this.saveUser(user);

      // Return normalized response with must_change_password for compatibility with Login component
      return {
        ...response.data,
        must_change_password: requiresPasswordChange
      };
    } catch (error) {
      throw new Error(ApiClient.getErrorMessage(error));
    }
  }

  public async logout(): Promise<void> {
    // Check if authenticated before clearing
    const wasAuthenticated = apiClient.isAuthenticated();

    // Immediately clear local data for instant logout
    apiClient.clearAuth();
    this.clearUser();
    clearSkipFlag(); // Clear room setup skip flag

    // Make logout API call in background (don't await)
    // This notifies the server to invalidate the token
    if (wasAuthenticated) {
      const logoutEndpoint = typeof BACKEND_CONFIG.endpoints.logout === 'function'
        ? BACKEND_CONFIG.endpoints.logout(BACKEND_CONFIG.isFastAPI)
        : BACKEND_CONFIG.endpoints.logout;

      apiClient.post(logoutEndpoint).catch(error => {
        logger.debug('Logout API call failed (non-critical):', error);
      });
    }
  }

  public async refreshToken(): Promise<void> {
    const refreshToken = sessionStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const refreshEndpoint = typeof BACKEND_CONFIG.endpoints.refresh === 'function'
        ? BACKEND_CONFIG.endpoints.refresh(BACKEND_CONFIG.isFastAPI)
        : BACKEND_CONFIG.endpoints.refresh;

      // FastAPI expects { refresh_token }, Django expects { refresh }
      const payload = BACKEND_CONFIG.isFastAPI
        ? { refresh_token: refreshToken }
        : { refresh: refreshToken };

      const response = await apiClient.post(refreshEndpoint, payload);

      const { access_token } = response.data;
      sessionStorage.setItem('access_token', access_token);
    } catch (error) {
      // If refresh fails, logout user
      this.logout();
      throw new Error('Session expired. Please log in again.');
    }
  }

  public async getCurrentUser(): Promise<User | null> {
    if (this.currentUser) {
      return this.currentUser;
    }

    if (!apiClient.isAuthenticated()) {
      return null;
    }

    // Don't fetch from API for referral users and team members - they use a different auth system
    // and their user data is already in localStorage from referral portal login
    const userData = localStorage.getItem('user');

    if (userData) {
      try {
        const user = JSON.parse(userData);

        if (user.user_type === 'referral' || user.user_type === 'referral_team') {
          this.currentUser = user;
          return user;
        }
      } catch (error) {
        logger.error('Failed to parse user data from localStorage', error);
      }
    }

    // Only fetch from staff API for staff users
    try {
      const meEndpoint = typeof BACKEND_CONFIG.endpoints.me === 'function'
        ? BACKEND_CONFIG.endpoints.me(BACKEND_CONFIG.isFastAPI)
        : BACKEND_CONFIG.endpoints.me;

      const response = await apiClient.get<User>(meEndpoint);
      this.saveUser(response.data);
      return response.data;
    } catch (error) {
      logger.error('Failed to fetch current user', error);
      this.logout();
      return null;
    }
  }

  public isAuthenticated(): boolean {
    return apiClient.isAuthenticated() && !!this.currentUser;
  }

  public getUser(): User | null {
    return this.currentUser;
  }

  public hasRole(roleType: string): boolean {
    // Referral users don't have roles
    if (!this.currentUser || this.currentUser.user_type === 'referral') return false;

    // Check roles array
    if ('roles' in this.currentUser && this.currentUser.roles && this.currentUser.roles.length > 0) {
      // Handle both string array and object array formats
      return this.currentUser.roles.some(r => {
        // If it's a string (FastAPI format)
        if (typeof r === 'string') {
          return r === roleType;
        }
        // If it's an object with role_type (Django format)
        return r.role_type === roleType;
      });
    }

    return false;
  }

  public hasAnyRole(roleTypes: string[]): boolean {
    // Referral users don't have roles
    if (!this.currentUser || this.currentUser.user_type === 'referral') return false;

    // Check roles array
    if ('roles' in this.currentUser && this.currentUser.roles && this.currentUser.roles.length > 0) {
      // Handle both string array and object array formats
      return this.currentUser.roles.some(r => {
        // If it's a string (FastAPI format)
        if (typeof r === 'string') {
          return roleTypes.includes(r);
        }
        // If it's an object with role_type (Django format)
        return roleTypes.includes(r.role_type);
      });
    }

    return false;
  }

  public hasPermission(permission: string): boolean {
    // Referral users don't have permissions
    if (!this.currentUser || this.currentUser.user_type === 'referral') return false;

    // Handle referral team member permissions (object-based)
    if (this.currentUser.user_type === 'referral_team' && 'permissions' in this.currentUser) {
      const perms = this.currentUser.permissions as Record<string, boolean>;
      return !!perms[permission];
    }

    // Handle staff permissions (array-based)
    if ('permissions' in this.currentUser && Array.isArray(this.currentUser.permissions)) {
      return this.currentUser.permissions.includes(permission);
    }

    return false;
  }

  public hasAnyPermission(permissions: string[]): boolean {
    // Referral users don't have permissions
    if (!this.currentUser || this.currentUser.user_type === 'referral') return false;

    // Handle referral team member permissions (object-based)
    if (this.currentUser.user_type === 'referral_team') {
      if ('permissions' in this.currentUser) {
        const perms = this.currentUser.permissions as Record<string, boolean>;
        return permissions.some(p => !!perms[p]);
      }
      return false;
    }

    // Handle staff permissions (array-based)
    if ('permissions' in this.currentUser && Array.isArray(this.currentUser.permissions)) {
      return permissions.some(p => (this.currentUser as any).permissions.includes(p));
    }

    return false;
  }

  public getDepartment(): string | null {
    // Referral users don't have departments
    if (this.currentUser?.user_type === 'referral') return null;

    return this.currentUser && 'department' in this.currentUser ? (this.currentUser?.department?.department_code || null) : null;
  }

  public getRoles(): string[] {
    // Referral users don't have roles
    if (this.currentUser?.user_type === 'referral') return [];

    if (this.currentUser && 'roles' in this.currentUser && this.currentUser?.roles && this.currentUser.roles.length > 0) {
      // Handle both string array and object array formats
      return this.currentUser.roles.map(r => {
        // If it's a string (FastAPI format)
        if (typeof r === 'string') {
          return r;
        }
        // If it's an object with role_type (Django format)
        return r.role_type;
      });
    }

    return [];
  }

  public getPermissions(): string[] {
    // Referral users don't have permissions
    if (this.currentUser?.user_type === 'referral') return [];

    // Handle referral team member permissions (object-based) - convert to array
    if (this.currentUser?.user_type === 'referral_team' && 'permissions' in this.currentUser) {
      const perms = this.currentUser.permissions as Record<string, boolean>;
      return Object.keys(perms).filter(key => perms[key]);
    }

    // Handle staff permissions (array-based)
    if (this.currentUser && 'permissions' in this.currentUser && Array.isArray(this.currentUser.permissions)) {
      return this.currentUser.permissions;
    }

    return [];
  }

  // Check if user can access a specific feature using permissions
  public canManagePatients(): boolean {
    return this.hasPermission('can_manage_patients');
  }

  public canManageQueue(): boolean {
    return this.hasAnyRole(['receptionist', 'technician', 'doctor', 'admin', 'super_admin']);
  }

  public canProcessPayments(): boolean {
    return this.hasPermission('can_process_payments');
  }

  public canViewReports(): boolean {
    return this.hasPermission('can_view_reports');
  }

  public canManageStaff(): boolean {
    return this.hasPermission('can_manage_users');
  }

  public canManageRoles(): boolean {
    return this.hasPermission('can_manage_roles');
  }

  public isSuperAdmin(): boolean {
    return this.hasRole('super_admin');
  }

  public isAdmin(): boolean {
    return this.hasAnyRole(['super_admin', 'admin']);
  }
}

export const authService = new AuthService();
export default authService;